import { HttpClient } from '@angular/common/http';
import { afterNextRender, computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError, concatMap, delay, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { IConnectionGetResponseDto, IConnectionPostResponseDto, IUserLostPasswordRequestDto, IUserLostPasswordResponseDto, IUserSetPasswordRequestDto } from '../../../shared/ystm-service-api-types';
import { AuthTokenProviderService } from '../../auth-jwt/services/auth-token-provider.service';
import { LogService } from '../../debug';
import { AuthState } from '../models/auth-state.model';
import { IUserCredentials } from '../models/user-credentials.model';
import { IUserClaimsModel, IUserModel, UserSalutationType, UserStatusType } from '../models/user.model';
/**
 * This service is used to manage login, logout and session management.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * DI
   */
  private _httpClient = inject(HttpClient);
  private _logService = inject(LogService);
  private _authTokenProviderService = inject(AuthTokenProviderService)

  /**
   * Behavior subject on AuthState
   */
  private _authState: WritableSignal<AuthState> = signal({ user: null });
  readonly authState = this._authState.asReadonly();

  public isLoggedIn = computed(() => {
    const authState = this.authState();
    return (authState.user !== null && authState.user !== undefined);
  });

  public authUserName = computed(() => {
    const authState = this.authState();
    return authState.user?.claims?.email ?? null;
  });

  public isAdmin = computed(() => {
    const authState = this.authState();
    return (authState.user?.claims?.roles?.split(',') ?? []).includes('ADMIN')
  });

  public isRoot = computed(() => {
    const authState = this.authState();
    return (authState.user?.claims?.roles?.split(',') ?? []).includes('ROOT')
  });

  public isAdminOrRoot = computed(() => {
    const authState = this.authState();
    return (authState.user?.claims?.roles?.split(',') ?? []).includes('ADMIN') || (authState.user?.claims?.roles?.split(',') ?? []).includes('ROOT');
  });

  public isGastronomyOrAdminOrRoot = computed(() => {
    const authState = this.authState();
    return (authState.user?.claims?.roles?.split(',') ?? []).includes('GASTRONOMY') || (authState.user?.claims?.roles?.split(',') ?? []).includes('ADMIN') || (authState.user?.claims?.roles?.split(',') ?? []).includes('ROOT');
  });

  isGastronomy = computed(() => {
    const authState = this.authState();
    return (authState.user?.claims?.roles?.split(',') ?? []).includes('GASTRONOMY');
  });

  async forRoot(): Promise<void> {
    //this._logService.log('AuthService.forRoot started');
    afterNextRender(async () => {
      try {
        const userInfoRes = await (lastValueFrom(this.userInfo()));
        //this._logService.log('User info retrieved:', userInfoRes);
        if (userInfoRes) {
          this._authState.set({
            user: {
              claims: (userInfoRes.claims as IUserClaimsModel)
            }
          });
        }
      } catch (e) {
        //this._logService.error('Error in AuthService.forRoot:', e);
      }
    });
    //this._logService.log('AuthService.forRoot finalized');
  }


  /**
   * login using simple auth
   * Logs in user. Should be triggered by side effect.
   * This method is triggered by a side effect of the store, slice 'auth'.
   * It a usr oriented implementation avoiding to some details such as connectivity check etc.
   * All this is hidden in Http interceptors.
   * It returns a UserModel or an error. The UserModel includes all necessary claims which are completed
   * by call to userGetDetails.
   * @param auth user credential as defined in store models.
   * 
   * Some Details:
   * UserLoginWithExternalCredentialsRequest
   * required from App: username, pw, optional PIN (see below)
 * do not set subUsername, tenant, osVersion
 * Set channel (on portal_layer!) to APP_VODAFONE
 * Set ip, set locale (enum: de, en, ...tbd...), appVersion (something like 1.0.1, must be configured in backend)
 *
 * Error codes:
 * 
 * From ecobill:
   * 8106 - App/Program version not valid
   * 8265 - User is obligated to provide additional login parameter or invalid PIN on Login with PIN
   * 8266 - User must first attend a training before use of this service
   * 8303 - Invalid tenant
   * 8603 - Invalid sales partner
 * From LIG:
   * 1026 - Too many similar requests within defined time period
   * 1305 - Database operation failed
   * 1400 - Function call not allowed
   * 1411 - External function call timed out
   * 1500 - Unspecified external error occured
   * 8251 - Missing external credentials
   * 8252 - Invalid external credentials
   * 8263 - User account is not activ
   * 8264 - User account is not allowed for this service
   * 8560 - External service call failed
   * 9005 - Missing parameter in request
   *
   * If code is 8265 then user has to provide PIN. Login again with pin as additional parameter. At comfortLogin you can send PIN also in first request, 8265 will then only occur if PIN is not correct.
   * If code is 8266 then user must view training video. Once this is done, try login again, set confirmTraining = true (only until login is successful, then skip or set it to false)
   */
  public login(credentials: IUserCredentials): Observable<AuthState> {
    //this._logService.log('AuthService.login', credentials);
    return this._httpClient.post<IConnectionPostResponseDto>(`${environment.ystmServiceBaseUrl}/auth/sign-in`, credentials).pipe(
      //tap(res => this._logService.log(res)),
      map(res => {
        const user: IUserModel = {
          claims: {
            ...res.claims,
            status: res.claims.status as UserStatusType,
            roles: res.claims.roles,
            salutation: res.claims.salutation as UserSalutationType,
            phone: res.claims.phone ? res.claims.phone : undefined,
          },
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
        };
        const nextAuthState: AuthState = {
          user
        };
        this._authTokenProviderService.setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken });
        this._authState.set(nextAuthState);
        return nextAuthState;
      }),
    )
  }

  public setPassword(credentials: { email: string; password: string }): Observable<AuthState> {
    const setPasswordRequest: IUserSetPasswordRequestDto = {
      passwordNew: credentials.password
    };
    //this._logService.log ('setPassword', credentials)
    return this._httpClient.post<void>(`${environment.ystmServiceBaseUrl}/user/set-password`, setPasswordRequest).pipe(
      delay(50), // add delay to allow for update of user status in backend, otherwise the subsequent login might fail
      concatMap(() => {
        const loginCredentials: IUserCredentials = {
          email: credentials.email,
          password: credentials.password
        };
        return this.login(loginCredentials);
      })
    );
  }

  public logout(): Observable<null> {
    // build pipeline to be subscribed by controller
    // Old implementation
    // return this._httpClient.delete(`${environment.serviceBaseUrl}/auth/connection`).pipe(
    //   map((res => null)),
    //   catchError(err => { return of(null) })
    // )

    return this._authTokenProviderService.signOut().pipe(
      map(() => {
        this._authState.set({ user: null });
        return null;
      }),
      catchError(() => {
        this._authState.set({ user: null });
        return of(null);
      })
    );
  }

  /**
   * method to initiate user lost password process
   */
  public lostPassword(email: string): Observable<IUserLostPasswordResponseDto> {
    this._authTokenProviderService.clearTokens();
    const req: IUserLostPasswordRequestDto = {
      email
    };
    return this._httpClient.post<IUserLostPasswordResponseDto>(`${environment.ystmServiceBaseUrl}/user/user-lost-password`, req);
  }

  /**
   * Method to retrieve user info
   * @returns {Observable<IConnectionGetResponseDto | null>}
   */
  public userInfo(): Observable<IConnectionGetResponseDto | null> {
    //this._logService.log('AuthService.userInfo(), getting user info');
    return this._httpClient.get<IConnectionGetResponseDto>(`${environment.ystmServiceBaseUrl}/auth/user-info`).pipe(
      catchError(() => {
        //this._logService.error('AuthService.userInfo(), error getting user info, error:', err);
        //console.log ('error getting user info', err)
        return of(null);
      })
    );
  }

}
